/**
 * addon-products.js
 *
 * Metafield-driven addon variant selection with AJAX multi-item cart submission.
 *
 * Compatibility targets (Step 10):
 *   ✓ Horizon OS 2.0 section/block rendering lifecycle
 *   ✓ <product-form-component> web component (buy-buttons.liquid)
 *   ✓ Cart drawer component – dispatches 'cart:update' (CartAddEvent.eventName)
 *   ✓ Dynamic checkout / accelerated-checkout buttons (bypasses this form entirely)
 *   ✓ Theme editor live preview (re-initialises on shopify:section:load / block:load)
 *   ✓ Shopify CLI / Git deployment workflow (plain ES module, no build step)
 *
 * Architecture:
 *   One AddonProductsManager instance per [data-addon-manager] container.
 *   The manager intercepts the parent <form data-type="add-to-cart-form"> submit
 *   event before it can bubble up to <product-form-component on:submit>.
 *   It then builds an items[] payload (main variant + selected addon variants)
 *   and POSTs it to /cart/add.js in a single request.
 *   After success it dispatches Horizon's 'cart:update' custom event so the
 *   cart-drawer-component opens and the cart bubble count updates.
 */

// ── Constants ───────────────────────────────────────────────────────────────

/**
 * Horizon cart update event name.
 * Defined as CartAddEvent.eventName = ThemeEvents.cartUpdate in assets/events.js.
 * cart-drawer-component listens for this to call showDialog().
 */
const CART_UPDATE_EVENT = 'cart:update';

/**
 * CSS selector for the Shopify product add-to-cart form.
 * The data-type attribute is set in blocks/buy-buttons.liquid:
 *   {%- form 'product', product, id: product_form_id, data-type: 'add-to-cart-form' -%}
 */
const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';

// ── AddonProductsManager class ───────────────────────────────────────────────

class AddonProductsManager {
  /**
   * @param {HTMLElement} container - Element with [data-addon-manager] attribute.
   *   Set by snippets/addon-products.liquid.
   */
  constructor(container) {
    /** @type {HTMLElement} */
    this.container = container;

    /**
     * Parent product title, read from data-product-title on the wrapper.
     * Set by Liquid: data-product-title="{{ product.title | escape }}"
     * Stamped on every addon line item as: { "Parent Product": this.productTitle }
     * @type {string}
     */
    this.productTitle = container.dataset.productTitle || '';

    /**
     * Number of physical items inside a bundle product.
     * Read from product.metafields.custom.bundle_item_count (integer metafield).
     * For non-bundle (single) products this defaults to 1, preserving existing behaviour.
     * For a 3-knife set bundle this would be 3, allowing 3 engraving rows per variant at qty=1.
     * @type {number}
     */
    this.bundleItemCount = parseInt(container.dataset.bundleItemCount, 10) || 1;

    /**
     * Tracks selected addon variant IDs.
     * Map<variantId: number, properties: Object>
     * Using Map (not Set) so we can store per-variant properties alongside IDs.
     * @type {Map<number, Object>}
     */
    this.selectedVariants = new Map();

    /**
     * The parent product form.
     *
     * The addon block is now rendered in sections/product-information.liquid
     * OUTSIDE the buy-buttons form (below the product media grid).
     * We can no longer use closest() so we find the form by its Horizon ID:
     *   buy-buttons.liquid: product_form_id = 'BuyButtons-ProductForm-' | section.id
     * Both the form and the addon manager share the same section.id, so the
     * data-section-id attribute on the wrapper gives us the lookup key.
     *
     * closest(FORM_SELECTOR) is kept as a fallback for any context where the
     * addon block is rendered inside a form (e.g. featured-product sections).
     * @type {HTMLFormElement|null}
     */
    const sectionId = container.dataset.sectionId;
    this.form = document.getElementById(`BuyButtons-ProductForm-${sectionId}`)
      || container.closest(FORM_SELECTOR);

    if (!this.form) {
      console.warn('[AddonProducts] Parent product form not found for section', sectionId);
      return;
    }

    // Step 4/5: Wire up toggle checkboxes for multi-select tracking
    this._bindToggleCheckboxes();

    // Live-validation: clear field errors as the user types or selects a font
    this._bindLiveValidation();

    // Wire up "+ Add another engraving" and row-level Remove buttons
    this._bindAddAnotherEngraving();

    // Font picker: open/close/select with per-option font-family display
    this._initFontPickers();

    // Load Google Fonts for the font picker options via Font Loading API
    this._loadAddonFonts();

    // Addon qty stepper: +/- buttons for non-engraving addon quantity
    this._bindAddonQtySteppers();

    // Step 6: Intercept the form submit before Horizon's component handler
    this._interceptFormSubmission();

    // Quantity-gating: restrict addon counts to main product quantity
    this._bindQuantityWatcher();
  }

  // ── Main product quantity helpers ─────────────────────────────────────────

  /**
   * Find the main-product quantity <input>.
   *
   * Horizon renders this input OUTSIDE the <form> DOM tree (linked via the
   * HTML form attribute). form.querySelector will NOT find it.
   *
   * @returns {HTMLInputElement|null}
   */
  _findMainQtyInput() {
    const formId = this.form?.id;
    if (formId) {
      const el = document.querySelector(`input[name="quantity"][form="${formId}"]`);
      if (el) return el;
    }
    return document.querySelector('quantity-selector-component input[name="quantity"]');
  }

  /**
   * Return the current main-product quantity.
   * @returns {number}
   */
  _getMainQuantity() {
    const input = this._findMainQtyInput();
    const val = parseInt(input?.value, 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  }

  /**
   * Return the maximum number of engraving rows allowed per variant.
   * For a non-bundle product (bundleItemCount=1) this equals the main qty.
   * For a bundle product (e.g. 3-knife set, bundleItemCount=3) at qty=1,
   * this returns 3 so each physical item in the bundle can be engraved.
   * @returns {number}
   */
  _getEngravingCap() {
    return this._getMainQuantity() * this.bundleItemCount;
  }

  // ── Step 4 / Step 5: Toggle selection tracking ────────────────────────────

  /**
   * Attach change listeners to all [data-addon-variant] checkboxes inside
   * this container.  Multi-select is allowed: each checkbox is independent.
   */
  _bindToggleCheckboxes() {
    this.container.querySelectorAll('[data-addon-variant]').forEach((toggle) => {
      toggle.addEventListener('change', () => this._handleToggleChange(toggle));
    });
  }

  /**
   * Handle a toggle checkbox state change:
   *   Checked   → add variant ID to selectedVariants Map, reveal extra fields
   *   Unchecked → remove variant ID from Map, hide extra fields
   *
   * @param {HTMLInputElement} checkbox
   */
  _handleToggleChange(checkbox) {
    const variantId = parseInt(checkbox.value, 10);

    if (checkbox.checked) {
      this.selectedVariants.set(variantId, {});
      this._toggleExtraFields(variantId, true);
      // For bundle products, add "Item N" labels to the template row
      if (this.bundleItemCount > 1) {
        this._renumberEngravingLabels(variantId);
      }
      // Sync caps against main product quantity
      this._syncAddAnotherButton(variantId);
      this._syncAddonQtyMax(variantId);
    } else {
      this.selectedVariants.delete(variantId);
      this._toggleExtraFields(variantId, false);
      // Reset any extra engraving rows added via "+ Add another engraving"
      // so the variant starts clean if the user re-checks it later
      this._resetEngravingRows(variantId);
    }
  }

  /**
   * Show or hide conditional extra-field panels (e.g. laser engraving inputs).
   * Fields are scoped to a specific variant via data-for-variant attribute.
   *
   * @param {number} variantId
   * @param {boolean} visible
   */
  _toggleExtraFields(variantId, visible) {
    // Toggle engraving fields (for engraving products)
    const fields = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    // Toggle qty row (for non-engraving addon products)
    const qtyRow = this.container.querySelector(
      `[data-addon-qty-row][data-for-variant="${variantId}"]`
    );
    if (qtyRow) {
      qtyRow.classList.toggle('hidden', !visible);
      // Reset qty to 1 when hiding
      if (!visible) {
        const qtyInput = qtyRow.querySelector('[data-addon-qty]');
        if (qtyInput) qtyInput.value = '1';
      }
    }
    if (!fields) return;
    fields.classList.toggle('hidden', !visible);
  }

  // ── Live validation: clear errors on input ─────────────────────────────────

  /**
   * Attach delegated listeners that clear validation errors as soon as the
   * user starts correcting a field.  Uses a single input listener on the
   * container (for text fields) and hooks into _selectFont for font pickers.
   */
  _bindLiveValidation() {
    this.container.addEventListener('input', (e) => {
      if (e.target.matches('[data-engraving-text]')) {
        this._clearFieldError(e.target);
      }
    });
  }

  // ── "+ Add another engraving" row management ──────────────────────────────

  /**
   * Delegate click events on the container for:
   *   [data-add-another-engraving]  → clone a new engraving row
   *   [data-remove-engraving-row]   → remove that specific row
   *
   * Using delegation (one listener on the container) so it handles both
   * Liquid-rendered buttons and buttons added dynamically by _addEngravingRow().
   */
  _bindAddAnotherEngraving() {
    this.container.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-another-engraving]');
      if (addBtn) {
        this._addEngravingRow(addBtn.dataset.forVariant);
        return;
      }
      const removeBtn = e.target.closest('[data-remove-engraving-row]');
      if (removeBtn) {
        this._removeEngravingRow(removeBtn.closest('[data-engraving-row]'));
      }
    });
  }

  /**
   * Clone the template engraving row and insert it before the actions bar.
   * The clone gets cleared values and a "Remove" button (the template row
   * never gets a Remove button — unchecking the toggle serves that purpose).
   *
   * @param {string} variantId - data-for-variant value from the clicked button
   */
  _addEngravingRow(variantId) {
    const container = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    if (!container) return;

    // Always clone the first row so we have a clean structure to duplicate
    const templateRow = container.querySelector('[data-engraving-row]');
    if (!templateRow) return;

    const newRow = templateRow.cloneNode(true);

    // Clear text/hidden inputs carried over from the clone source
    newRow.querySelectorAll('input[type="text"], input[type="hidden"]')
      .forEach((input) => (input.value = ''));
    // Reset the custom font picker UI so the clone shows "Select Font" placeholder
    this._resetFontPickerUI(newRow);

    // Add a Remove button — only on additional rows, not the template
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'addon-engraving-row-remove';
    removeBtn.setAttribute('data-remove-engraving-row', '');
    removeBtn.textContent = 'Remove';
    newRow.appendChild(removeBtn);

    // Insert before the actions div so rows stack above the "Add another" button
    const actionsDiv = container.querySelector('.addon-engraving-actions');
    container.insertBefore(newRow, actionsDiv);

    // For bundle products, renumber the "Item N" labels on all rows
    if (this.bundleItemCount > 1) {
      this._renumberEngravingLabels(variantId);
    }

    // Re-sync: hide button if cap reached after adding this row
    this._syncAddAnotherButton(variantId);
  }

  /**
   * Remove a dynamically added engraving row.
   * Guards against removing the last remaining row (the template must stay).
   *
   * @param {HTMLElement|null} row - The [data-engraving-row] element to remove
   */
  _removeEngravingRow(row) {
    if (!row) return;
    const container = row.closest('[data-engraving-fields]');
    if (!container) return;
    // Never remove if only one row remains
    if (container.querySelectorAll('[data-engraving-row]').length <= 1) return;
    const variantId = container.dataset.forVariant;
    row.remove();

    // For bundle products, renumber the "Item N" labels after removal
    if (this.bundleItemCount > 1) {
      this._renumberEngravingLabels(variantId);
    }

    // Re-sync: show button again now that we're below the cap
    this._syncAddAnotherButton(variantId);
  }

  /**
   * Remove all extra engraving rows for a variant, leaving only the template
   * (first) row, and clear its input values.
   * Called when a variant is unchecked or after a successful cart add.
   *
   * @param {number} variantId
   */
  _resetEngravingRows(variantId) {
    const container = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    if (!container) return;

    // Remove all rows beyond the first
    const rows = [...container.querySelectorAll('[data-engraving-row]')];
    rows.slice(1).forEach((row) => row.remove());

    // Clear values in the remaining template row (text input + font picker)
    const templateRow = container.querySelector('[data-engraving-row]');
    if (templateRow) {
      templateRow.querySelectorAll('input[type="text"], input[type="hidden"]')
        .forEach((input) => (input.value = ''));
      this._resetFontPickerUI(templateRow);
    }
  }

  /**
   * Add or update "Item N" labels on each engraving row for a variant.
   * Only meaningful for bundle products (bundleItemCount > 1).
   * Ensures labels stay sequential after rows are added or removed.
   *
   * @param {string|number} variantId
   */
  _renumberEngravingLabels(variantId) {
    const container = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    if (!container) return;

    const rows = container.querySelectorAll('[data-engraving-row]');
    rows.forEach((row, i) => {
      let label = row.querySelector('.addon-engraving-item-label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'addon-engraving-item-label';
        row.prepend(label);
      }
      label.textContent = `Item ${i + 1}`;
    });
  }

  // ── Quantity-gating: restrict addons to main product qty ────────────────

  /**
   * Show or hide the "+ Add another engraving" button for a variant.
   *   – main qty == 1  → hidden (only 1 knife, 1 engraving row is enough)
   *   – rows >= main qty → hidden (cap reached)
   *   – rows < main qty  → visible
   *
   * @param {string|number} variantId
   */
  _syncAddAnotherButton(variantId) {
    const container = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    if (!container) return;

    const cap = this._getEngravingCap();
    const rowCount = container.querySelectorAll('[data-engraving-row]').length;
    const btn = container.querySelector('[data-add-another-engraving]');
    if (!btn) return;

    btn.hidden = cap <= 1 || rowCount >= cap;
  }

  /**
   * Trim engraving rows down to the main qty cap if it was reduced.
   * Keeps the first (template) row intact.
   *
   * @param {string|number} variantId
   * @param {number} maxRows
   */
  _trimEngravingRows(variantId, maxRows) {
    const container = this.container.querySelector(
      `[data-engraving-fields][data-for-variant="${variantId}"]`
    );
    if (!container) return;

    const rows = [...container.querySelectorAll('[data-engraving-row]')];
    rows.slice(Math.max(1, maxRows)).forEach((row) => row.remove());
  }

  /**
   * Set the max attribute on an addon qty input so it cannot exceed the
   * effective addon budget. For non-bundle products this equals the main
   * qty. For bundle products (e.g. a 3-knife set, bundleItemCount=3) the
   * budget is main qty × bundleItemCount — same formula used by
   * _getEngravingCap() for engraving rows, now also applied to regular
   * addon qty inputs so that a 2-item bundle at main qty 1 correctly
   * allows addon qty up to 2 (not 1).
   *
   * @param {string|number} variantId
   */
  _syncAddonQtyMax(variantId) {
    const input = this.container.querySelector(
      `[data-addon-qty][data-for-variant="${variantId}"]`
    );
    if (!input) return;

    const maxQty = this._getMainQuantity() * this.bundleItemCount;
    input.max = maxQty;

    // Clamp current value if it exceeds the new max
    const current = parseInt(input.value, 10) || 1;
    if (current > maxQty) {
      input.value = maxQty;
    }

    // Update stepper button disabled states
    this._updateStepperButtons(input);
  }

  /**
   * Enable/disable the +/- buttons around an addon qty input based on
   * current value vs min/max.
   *
   * @param {HTMLInputElement} input
   */
  _updateStepperButtons(input) {
    const stepper = input?.closest('.addon-qty-stepper');
    if (!stepper) return;

    const val = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 1;

    const minusBtn = stepper.querySelector('[data-addon-qty-minus]');
    const plusBtn = stepper.querySelector('[data-addon-qty-plus]');

    if (minusBtn) minusBtn.disabled = val <= min;
    if (plusBtn) plusBtn.disabled = val >= max;
  }

  /**
   * Delegate click events for addon qty stepper +/- buttons.
   */
  _bindAddonQtySteppers() {
    this.container.addEventListener('click', (e) => {
      const plus = e.target.closest('[data-addon-qty-plus]');
      const minus = e.target.closest('[data-addon-qty-minus]');

      if (!plus && !minus) return;

      const stepper = (plus || minus).closest('.addon-qty-stepper');
      const input = stepper?.querySelector('[data-addon-qty]');
      if (!input) return;

      const val = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 1;

      if (plus && val < max) {
        input.value = val + 1;
      } else if (minus && val > min) {
        input.value = val - 1;
      }

      this._updateStepperButtons(input);
    });
  }

  /**
   * Re-sync all checked addon variants against the current main qty.
   * Called whenever the main product quantity changes.
   */
  _syncAllAddons() {
    const cap = this._getEngravingCap();

    this.selectedVariants.forEach((_, variantId) => {
      // Engraving: trim rows + sync button (uses bundle-aware cap)
      this._trimEngravingRows(variantId, cap);
      this._syncAddAnotherButton(variantId);

      // Non-engraving: clamp qty input max
      this._syncAddonQtyMax(variantId);
    });
  }

  /**
   * Watch the main product quantity input for changes.
   * Horizon dispatches 'quantity-selector:update' when +/- buttons are clicked.
   * We also listen for native input/change as a keyboard-edit fallback.
   */
  _bindQuantityWatcher() {
    const sync = () => this._syncAllAddons();

    // Horizon's QuantitySelectorUpdateEvent bubbles to document
    document.addEventListener('quantity-selector:update', sync);

    // Fallback: native events on the <input> itself (keyboard edits)
    const qtyInput = this._findMainQtyInput();
    if (qtyInput) {
      qtyInput.addEventListener('input', sync);
      qtyInput.addEventListener('change', sync);
    }
  }

  // ── Google Fonts dynamic loading ──────────────────────────────────────────

  /**
   * Load Google Fonts for the font picker options using the Font Loading API.
   *
   * Reads font names from the data-addon-fonts JSON attribute on the wrapper.
   * Uses document.fonts.load() to trigger font loading and removes the
   * .fonts-loading class once all fonts are ready (or after timeout).
   *
   * This complements the <link rel="stylesheet" media="print" onload> tag
   * injected by Liquid — together they ensure:
   *   1. CSS @font-face rules are available (from Google Fonts stylesheet)
   *   2. JS detects when fonts are actually rendered (Font Loading API)
   *   3. Fallback: .fonts-loading is removed after 3s even if fonts fail
   */
  _loadAddonFonts() {
    const fontsJson = this.container.dataset.addonFonts;
    if (!fontsJson) {
      this.container.classList.remove('fonts-loading');
      return;
    }

    let fontNames;
    try {
      fontNames = JSON.parse(fontsJson);
    } catch {
      this.container.classList.remove('fonts-loading');
      return;
    }

    if (!fontNames || !fontNames.length) {
      this.container.classList.remove('fonts-loading');
      return;
    }

    // Use Font Loading API if available, otherwise rely on CSS link onload
    if (typeof document.fonts?.load === 'function') {
      const fontPromises = fontNames.map((name) =>
        document.fonts.load(`1em "${name}"`).catch(() => null)
      );

      // Remove .fonts-loading when all fonts are ready or after 3s timeout
      const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
      Promise.race([Promise.all(fontPromises), timeout]).then(() => {
        this.container.classList.remove('fonts-loading');
      });
    } else {
      // Fallback: remove after a short delay for the CSS link to load
      setTimeout(() => {
        this.container.classList.remove('fonts-loading');
      }, 1500);
    }
  }

  // ── Font picker ────────────────────────────────────────────────────────────

  /**
   * Initialise the custom font picker dropdown using event delegation on the
   * container.  A single delegated listener handles all pickers, including
   * ones cloned later by _addEngravingRow().
   *
   * Each picker structure (from the snippet):
   *   [data-font-picker]
   *     input[type=hidden][data-engraving-font]  ← value read at cart submit
   *     button[data-font-trigger]
   *       span[data-font-trigger-label]          ← shows selected font name
   *     ul[data-font-options]
   *       li[data-font-option="FontName"]        ← styled in its own font-family
   */
  _initFontPickers() {
    // Delegated listener on the addon manager container
    this.container.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-font-trigger]');
      if (trigger) {
        e.stopPropagation();
        this._toggleFontDropdown(trigger.closest('[data-font-picker]'));
        return;
      }

      const option = e.target.closest('[data-font-option]');
      if (option) {
        e.stopPropagation();
        this._selectFont(
          option.closest('[data-font-picker]'),
          option.dataset.fontOption
        );
        return;
      }

      // Click anywhere else in the container → close all open pickers
      this._closeAllFontDropdowns();
    });

    // Keyboard support for the listbox pattern (WCAG 2.1.1 / 4.1.2).
    // Additive: only acts on font-trigger / font-option targets.
    this.container.addEventListener('keydown', (e) => this._handleFontKeydown(e));

    // Close open pickers when clicking outside the addon manager
    this._boundDocClickForFonts = (e) => {
      if (!e.target.closest('[data-addon-manager]')) {
        this._closeAllFontDropdowns();
      }
    };
    document.addEventListener('click', this._boundDocClickForFonts);
  }

  /**
   * Toggle a specific font picker dropdown open or closed.
   * Closes all other open pickers first so only one is open at a time.
   *
   * @param {HTMLElement} picker - [data-font-picker] element
   */
  _toggleFontDropdown(picker) {
    if (!picker) return;
    const optionsList = picker.querySelector('[data-font-options]');
    const trigger = picker.querySelector('[data-font-trigger]');
    const isOpen = !optionsList.classList.contains('hidden');

    // Close all open pickers, then open this one if it was closed
    this._closeAllFontDropdowns();
    if (!isOpen) {
      optionsList.classList.remove('hidden');
      trigger?.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Close all open font pickers within this addon manager container.
   */
  _closeAllFontDropdowns() {
    this.container.querySelectorAll('[data-font-options]').forEach((list) => {
      list.classList.add('hidden');
      list.closest('[data-font-picker]')
        ?.querySelector('[data-font-trigger]')
        ?.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Keyboard handler for the font listbox (delegated on the container).
   * Trigger: ArrowDown/ArrowUp opens the list and moves focus into it.
   *   (Enter/Space on the trigger keep using the native <button> click to open,
   *    so we don't double-toggle it here.)
   * Options: Up/Down/Home/End move focus, Enter/Space select, Esc/Tab close.
   *
   * @param {KeyboardEvent} e
   */
  _handleFontKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);

    const trigger = target.closest('[data-font-trigger]');
    if (trigger) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const picker = trigger.closest('[data-font-picker]');
        this._openFontDropdown(picker);
        const options = this._getFontOptions(picker);
        if (!options.length) return;
        const selected = picker.querySelector('[data-font-option][aria-selected="true"]');
        let index = e.key === 'ArrowUp' ? options.length - 1 : 0;
        if (selected) index = options.indexOf(selected);
        this._focusFontOption(options, index);
      }
      return;
    }

    const option = target.closest('[data-font-option]');
    if (!option) return;

    const picker = option.closest('[data-font-picker]');
    const options = this._getFontOptions(picker);
    const index = options.indexOf(option);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._focusFontOption(options, Math.min(index + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._focusFontOption(options, Math.max(index - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        this._focusFontOption(options, 0);
        break;
      case 'End':
        e.preventDefault();
        this._focusFontOption(options, options.length - 1);
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        this._selectFont(picker, option.dataset.fontOption);
        picker.querySelector('[data-font-trigger]')?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        this._closeAllFontDropdowns();
        picker.querySelector('[data-font-trigger]')?.focus();
        break;
      case 'Tab':
        // Let focus leave naturally, but close the open list behind it.
        this._closeAllFontDropdowns();
        break;
      default:
        break;
    }
  }

  /**
   * Open a specific font picker dropdown (without toggling it closed).
   * Closes any other open pickers first.
   *
   * @param {HTMLElement} picker - [data-font-picker] element
   */
  _openFontDropdown(picker) {
    if (!picker) return;
    this._closeAllFontDropdowns();
    picker.querySelector('[data-font-options]')?.classList.remove('hidden');
    picker.querySelector('[data-font-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {HTMLElement} picker - [data-font-picker] element
   * @returns {HTMLElement[]} the option elements in DOM order
   */
  _getFontOptions(picker) {
    return Array.from(picker?.querySelectorAll('[data-font-option]') || []);
  }

  /**
   * Move roving focus to the option at `index`. Options are not natively
   * focusable, so we set tabindex=-1 on demand before focusing.
   *
   * @param {HTMLElement[]} options
   * @param {number}        index
   */
  _focusFontOption(options, index) {
    const option = options[index];
    if (!option) return;
    option.setAttribute('tabindex', '-1');
    option.focus();
  }

  /**
   * Commit a font selection:
   *   – Set the hidden input value (read by _buildCartItems at submit)
   *   – Update the trigger label text + font-family so it previews the selection
   *   – Mark the option as aria-selected
   *   – Close the dropdown
   *
   * @param {HTMLElement} picker   - [data-font-picker] element
   * @param {string}      fontName - font name from data-font-option attribute
   */
  _selectFont(picker, fontName) {
    if (!picker || !fontName) return;

    // Clear validation error on the font trigger when a font is selected
    const trigger = picker.querySelector('[data-font-trigger]');
    this._clearFieldError(trigger);

    // Update the hidden input value (cart submission reads this)
    const hiddenInput = picker.querySelector('[data-engraving-font]');
    if (hiddenInput) hiddenInput.value = fontName;

    // Find the selected option to read its CSS font-family
    const selectedOption = picker.querySelector(`[data-font-option="${CSS.escape(fontName)}"]`);
    const cssFontFamily = selectedOption?.dataset.fontFamily || `'${fontName}', serif`;

    // Update trigger label: display the font name styled in that font
    const label = picker.querySelector('[data-font-trigger-label]');
    if (label) {
      label.textContent = fontName;
      label.style.fontFamily = cssFontFamily;
    }

    // Update aria-selected on options
    picker.querySelectorAll('[data-font-option]').forEach((opt) => {
      opt.setAttribute(
        'aria-selected',
        opt.dataset.fontOption === fontName ? 'true' : 'false'
      );
    });

    // Close the dropdown
    picker.querySelector('[data-font-options]')?.classList.add('hidden');
    picker.querySelector('[data-font-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Reset a font picker UI back to the "Select Font" placeholder state.
   * Called when engraving rows are reset (uncheck / after cart add).
   *
   * @param {HTMLElement} row - [data-engraving-row] element containing the picker
   */
  _resetFontPickerUI(row) {
    const picker = row?.querySelector('[data-font-picker]');
    if (!picker) return;

    // Clear hidden input
    const hiddenInput = picker.querySelector('[data-engraving-font]');
    if (hiddenInput) hiddenInput.value = '';

    // Reset trigger label text and font-family
    const label = picker.querySelector('[data-font-trigger-label]');
    if (label) {
      label.textContent = 'Select Font';
      label.style.fontFamily = '';
    }

    // Clear aria-selected on all options
    picker.querySelectorAll('[data-font-option]').forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
    });

    // Ensure dropdown is closed
    picker.querySelector('[data-font-options]')?.classList.add('hidden');
    picker.querySelector('[data-font-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  // ── Step 6: Safe Add-to-Cart interception ─────────────────────────────────

  /**
   * Register a submit listener on the <form> element.
   *
   * Why we listen on the form (not the component):
   *   <product-form-component on:submit="/handleSubmit"> wraps the form.
   *   The submit event fires on the form, then bubbles UP to the component.
   *   By listening directly on the form and calling event.stopPropagation(),
   *   the event never reaches the component's on:submit handler, so we own
   *   the entire cart submission.
   *
   * Why NOT capture phase:
   *   capture:true would fire as the event travels DOWN to the form, before
   *   the form's own listeners – we'd stop the event before even the form's
   *   native submit fires.  Instead we listen in the default bubble phase
   *   (at-target for the form element), which is the correct insertion point.
   *
   * Dynamic checkout compatibility:
   *   Shopify's accelerated-checkout buttons (Apple Pay, Shop Pay, etc.) submit
   *   via the Payment Request API, completely bypassing this <form>.  They are
   *   unaffected by this listener.
   */
  _interceptFormSubmission() {
    this._boundHandleSubmit = this._handleFormSubmit.bind(this);
    // Use capture phase so our listener fires before any bubble-phase listeners
    // (including Horizon's product-form-component on:submit handler).
    // Combined with stopImmediatePropagation() inside the handler, this ensures
    // we are the sole handler that processes the submit event.
    this.form.addEventListener('submit', this._boundHandleSubmit, { capture: true });

    // Register this manager against its form so the patched
    // AddToCartComponent.handleClick (installed below) can find us and
    // short-circuit the animation + fly-to-cart when engraving validation
    // fails. We store the reference on the form element because the patched
    // handleClick runs on the <add-to-cart-component> instance and needs a
    // deterministic way to locate the matching manager.
    /** @type {any} */ (this.form).__addonProductsManager = this;

    AddonProductsManager._patchAddToCartHandleClick();
  }

  /**
   * One-time monkey-patch of Horizon's AddToCartComponent.prototype.handleClick
   * so that engraving validation runs BEFORE the checkmark + fly-to-cart
   * animations. We cannot intercept this via a DOM event listener:
   *
   *   assets/component.js registers Horizon's on:click delegator on
   *   `document` with { capture: true }. Document is the top of the DOM
   *   tree, so no listener on any descendant (form, button, etc.) can fire
   *   before it — capture phase starts at document. Same-target same-phase
   *   listeners fire in registration order, and Horizon registers during
   *   its first connectedCallback which predates this deferred module's
   *   initialisation. Patching the method itself is the reliable hook.
   *
   * The patch is installed exactly once, guarded by a flag on the class.
   *
   * @private
   */
  static _patchAddToCartHandleClick() {
    if (typeof window === 'undefined' || !window.customElements) return;

    const install = () => {
      const AddToCartClass = window.customElements.get('add-to-cart-component');
      if (!AddToCartClass || AddToCartClass.prototype.__addonEngravingPatched) return;

      const originalHandleClick = AddToCartClass.prototype.handleClick;
      if (typeof originalHandleClick !== 'function') return;

      AddToCartClass.prototype.handleClick = function patchedHandleClick(event) {
        try {
          const form = this.closest?.('form[data-type="add-to-cart-form"]');
          const manager = form ? /** @type {any} */ (form).__addonProductsManager : null;

          if (manager && manager.selectedVariants && manager.selectedVariants.size > 0) {
            const { valid } = manager._validateEngravingFields();
            if (!valid) {
              // Stop the event so:
              //   1. The implicit form submission from this submit-button click
              //      is cancelled → _handleFormSubmit / cart POST never runs.
              //   2. Any other delegated click listeners downstream are skipped.
              // The toast surfaced by _validateEngravingFields() is the only
              // user feedback. The button stays in its default (non-added)
              // state because we return before invoking the original
              // handleClick, which is what toggles dataset.added + fly-to-cart.
              if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
              }
              if (event && typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
              }
              if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
              }
              return;
            }
          }
        } catch (err) {
          // Never let a defensive failure in our guard break the native flow.
          console.error('[AddonProducts] engraving pre-click guard failed:', err);
        }

        return originalHandleClick.apply(this, arguments);
      };

      AddToCartClass.prototype.__addonEngravingPatched = true;
    };

    // The custom element may not be defined yet when the first addon manager
    // initialises (module load order is not guaranteed). whenDefined resolves
    // immediately if it is already registered.
    window.customElements.whenDefined('add-to-cart-component').then(install).catch(() => {});
    // Also attempt synchronous install in case product-form.js has already run.
    install();
  }

  // ── Step 7 / Step 8 / Step 9: AJAX cart submission ────────────────────────

  /**
   * Form submit handler: prevents native submit + Horizon component handler,
   * builds cart payload, and POSTs to /cart/add.js.
   *
   * @param {SubmitEvent} event
   */
  async _handleFormSubmit(event) {
    // Prevent native browser form POST (page navigation)
    event.preventDefault();

    // Prevent the event from reaching any other submit listeners – both on the
    // same element (stopImmediatePropagation) and on ancestor elements (bubbling).
    // stopPropagation() alone only stops bubbling; if Horizon also attaches a
    // submit listener directly to the <form> element, both would fire on the
    // same target and the main product would be added twice.
    event.stopImmediatePropagation();

    const submitButton = this.form.querySelector('[name="add"]');
    this._setLoadingState(submitButton, true);

    try {
      // Validate engraving fields before building the cart payload.
      // Blocks submission if any checked engraving variant has empty text or no font.
      const validation = this._validateEngravingFields();
      if (!validation.valid) {
        this._setLoadingState(submitButton, false);
        return;
      }

      // Step 7a: Build items[] array (main product + selected addon variants)
      const items = this._buildCartItems();

      // Step 7b: POST all items to /cart/add.js in one request
      const cartAddResponse = await this._postToCart(items);

      // Step 10: Trigger Horizon cart drawer open + icon count refresh
      await this._dispatchCartUpdate(cartAddResponse);

      // UX: reset addon selections after a successful add
      this._clearSelections();

    } catch (err) {
      console.error('[AddonProducts] Cart submission failed:', err);
      this._showError(err.message || 'Could not add items to cart. Please try again.');
    } finally {
      this._setLoadingState(submitButton, false);
    }
  }

  // ── Engraving field validation ──────────────────────────────────────────

  /**
   * Validate that every checked engraving variant has all required fields
   * filled in every engraving row (text + font).
   *
   * On failure: highlights invalid fields with .addon-field-error, displays
   * an inline error message inside the engraving fields container, and
   * scrolls the first error into view.
   *
   * @returns {{ valid: boolean }}
   */
  _validateEngravingFields() {
    let valid = true;
    let firstErrorEl = null;

    // Clear any previous validation errors before re-checking
    this._clearAllValidationErrors();

    for (const [variantId] of this.selectedVariants) {
      const engravingContainer = this.container.querySelector(
        `[data-engraving-fields][data-for-variant="${variantId}"]`
      );
      if (!engravingContainer) continue;

      const rows = engravingContainer.querySelectorAll('[data-engraving-row]');
      rows.forEach((row) => {
        const textInput = row.querySelector('[data-engraving-text]');
        const fontInput = row.querySelector('[data-engraving-font]');
        const fontTrigger = row.querySelector('[data-font-trigger]');

        const textEmpty = !textInput?.value.trim();
        const fontEmpty = !fontInput?.value;

        if (textEmpty) {
          textInput?.classList.add('addon-field-error');
          valid = false;
          if (!firstErrorEl) firstErrorEl = textInput;
        }

        if (fontEmpty) {
          fontTrigger?.classList.add('addon-field-error');
          valid = false;
          if (!firstErrorEl) firstErrorEl = fontTrigger;
        }
      });
    }

    // Show a single centered toast instead of the old inline red error messages.
    // A single toast covers all variants/rows so repeated validation failures
    // don't stack duplicate notifications.
    if (!valid) {
      AddonProductsManager._showEngravingToast(
        'Please enter engraving text and select a font for each row.'
      );
    }

    // Scroll the first invalid field into view so the user sees the problem
    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return { valid };
  }

  /**
   * Shows a centered, auto-dismissing toast notification.
   * Static so it's shared across all AddonProductsManager instances and can
   * only ever produce one visible toast at a time (no duplicate stacking).
   *
   * @param {string} message - Plain text message to display.
   * @param {number} [duration=5000] - Milliseconds before auto-dismiss.
   */
  static _showEngravingToast(message, duration = 5000) {
    // Inject toast stylesheet once per page
    if (!document.getElementById('engraving-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'engraving-toast-styles';
      style.textContent = `
        .engraving-toast {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0.96);
          z-index: 2147483000;
          max-width: calc(100vw - 32px);
          padding: 16px 28px;
          background: rgba(30, 30, 30, 0.92);
          color: #ffffff;
          font-size: 15px;
          line-height: 1.4;
          font-weight: 500;
          text-align: center;
          white-space: nowrap;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
          opacity: 0;
          pointer-events: none;
          transition: opacity 180ms ease-out, transform 180ms ease-out;
        }
        .engraving-toast.is-visible {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
        @media (max-width: 480px) {
          .engraving-toast {
            white-space: normal;
            font-size: 14px;
            padding: 14px 20px;
            max-width: calc(100vw - 48px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .engraving-toast {
            transition: opacity 80ms linear;
            transform: translate(-50%, -50%);
          }
          .engraving-toast.is-visible {
            transform: translate(-50%, -50%);
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Reuse a single toast node to guarantee no duplicates / layout shift.
    let toast = document.getElementById('engraving-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'engraving-toast';
      toast.className = 'engraving-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toast);
    }

    // Clear any pending dismiss timer from a previous invocation
    if (AddonProductsManager._engravingToastTimer) {
      clearTimeout(AddonProductsManager._engravingToastTimer);
      AddonProductsManager._engravingToastTimer = null;
    }

    toast.textContent = message;
    // Force reflow so the fade-in transition re-runs on repeat triggers
    // eslint-disable-next-line no-unused-expressions
    toast.offsetHeight;
    toast.classList.add('is-visible');

    AddonProductsManager._engravingToastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      AddonProductsManager._engravingToastTimer = null;
    }, duration);
  }

  /**
   * Remove all validation error highlights and messages from the container.
   */
  _clearAllValidationErrors() {
    this.container.querySelectorAll('.addon-field-error').forEach((el) => {
      el.classList.remove('addon-field-error');
    });
    this.container.querySelectorAll('[data-engraving-error]').forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });
  }

  /**
   * Clear validation error on a single field when the user corrects it.
   * Called from live-validation listeners on text input and font selection.
   *
   * @param {HTMLElement} field - The input or trigger element to clear
   */
  _clearFieldError(field) {
    if (!field) return;
    field.classList.remove('addon-field-error');

    // If all fields in this variant's container are now valid, hide the message
    const engravingContainer = field.closest('[data-engraving-fields]');
    if (engravingContainer && !engravingContainer.querySelector('.addon-field-error')) {
      const msgEl = engravingContainer.querySelector('[data-engraving-error]');
      if (msgEl) {
        msgEl.textContent = '';
        msgEl.hidden = true;
      }
    }
  }

  /**
   * Build the /cart/add.js `items` payload.
   *
   *   items[0]  : Main product variant (from the form hidden input[name="id"])
   *   items[1+] : Each selected addon variant with line item properties
   *
   * Step 8: Each addon item receives { "Parent Product": product.title }
   * as a mandatory line item property for parent-product traceability.
   *
   * @returns {Array<{id: number, quantity: number, properties?: Object}>}
   */
  _buildCartItems() {
    // ── Main product variant ──────────────────────────────────────────────────
    const mainVariantId = parseInt(
      this.form.querySelector('[name="id"]')?.value || '0',
      10
    );

    const quantity = this._getMainQuantity();

    if (!mainVariantId) {
      throw new Error('[AddonProducts] No main variant ID found in product form.');
    }

    // Main item: _main_product is a hidden property (underscore prefix is not
    // displayed to the customer) used by component-cart-items.js to identify the
    // parent row when enforcing addon quantity limits on the cart page.
    const items = [{ id: mainVariantId, quantity, properties: { _main_product: true } }];

    // ── Step 9: Addon variants as separate line items ─────────────────────────
    for (const [variantId] of this.selectedVariants) {
      const engravingContainer = this.container.querySelector(
        `[data-engraving-fields][data-for-variant="${variantId}"]`
      );

      if (engravingContainer) {
        /**
         * Engraving product: each [data-engraving-row] becomes its own cart
         * line item so the customer can have different text per row.
         * e.g. checking "On Knife" + clicking "+ Add another engraving" creates
         * two separate line items: one per filled row.
         *
         * For bundle products (bundleItemCount > 1) each row also gets a
         * "Bundle Item" property (e.g. "Item 1", "Item 2") so the merchant
         * knows which physical knife in the set the engraving belongs to.
         */
        const rows = engravingContainer.querySelectorAll('[data-engraving-row]');
        rows.forEach((row, index) => {
          const text = row.querySelector('[data-engraving-text]')?.value.trim() || '';
          const font = row.querySelector('[data-engraving-font]')?.value || '';
          const properties = { 
            'Parent Product': this.productTitle,
            _addon_product: true,
          };
          if (this.bundleItemCount > 1) {
            properties['Bundle Item'] = `Item ${index + 1}`;
          }
          if (text) properties['Engraving Text'] = text;
          if (font) properties['Engraving Font'] = font;
          items.push({ id: variantId, quantity: 1, properties });
        });
      } else {
        // Regular addon: read qty from the addon qty input (defaults to 1)
        const qtyInput = this.container.querySelector(
          `[data-addon-qty][data-for-variant="${variantId}"]`
        );
        const addonQty = parseInt(qtyInput?.value, 10) || 1;
        items.push({
          id: variantId,
          quantity: addonQty,
          properties: { 'Parent Product': this.productTitle, _addon_product: true },
        });
      }
    }

    return items;
  }

  /**
   * Collect line item properties for a given addon variant.
   *
   * Step 8 implementation:
   *   Always injects "Parent Product": productTitle for traceability.
   *   Also collects optional engraving fields if present and filled.
   *
   * @param {number} variantId
   * @returns {Object} Shopify line item properties object
   */
  _collectVariantProperties(variantId) {
    // Required: stamp the parent product title on every addon line item
    const properties = {
      'Parent Product': this.productTitle,
    };

    // Optional: laser engraving text (addon-engraving-text input)
    const engravingText = this.container.querySelector(
      `[data-engraving-text][data-variant-id="${variantId}"]`
    );
    // Optional: engraving font selection
    const engravingFont = this.container.querySelector(
      `[data-engraving-font][data-variant-id="${variantId}"]`
    );

    if (engravingText?.value.trim()) {
      properties['Engraving Text'] = engravingText.value.trim();
    }
    if (engravingFont?.value) {
      properties['Engraving Font'] = engravingFont.value;
    }

    return properties;
  }

  /**
   * POST to Shopify's AJAX Cart API (/cart/add.js).
   *
   * The `items` array format (vs. the legacy `id` + `quantity` flat format)
   * is required to add multiple products in a single request.
   *
   * Shopify docs: https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   *
   * @param {Array} items
   * @returns {Promise<Object>} Parsed /cart/add.js JSON response
   * @throws {Error} On non-2xx response
   */
  async _postToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // X-Requested-With signals to Shopify that this is an AJAX call
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      // Shopify returns structured error JSON: { status, message, description }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.description || errorData.message || `HTTP ${response.status}`
      );
    }

    return response.json();
  }

  // ── Step 10: Cart drawer + Horizon event dispatch ─────────────────────────

  /**
   * After a successful cart add, dispatch Horizon's 'cart:update' custom event
   * (CartAddEvent.eventName from assets/events.js) so:
   *   – cart-drawer-component.#handleCartAdd() fires → calls showDialog() (opens drawer)
   *   – Any other 'cart:update' listeners (cart bubble count, etc.) receive fresh data
   *
   * We also fetch /cart.js to include the latest item_count in the event detail,
   * matching the structure CartAddEvent uses so all Horizon listeners are satisfied.
   *
   * @param {Object} _addResponse - Response from /cart/add.js (unused but kept for future use)
   */
  async _dispatchCartUpdate(_addResponse) {
    // Fetch current cart state for accurate item count in event detail
    let cartData = {};
    try {
      const cartResponse = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      cartData = await cartResponse.json();
    } catch (_) {
      // Non-fatal: cart event will still fire; item count may be momentarily stale
    }

    /**
     * Mirror CartAddEvent detail structure from assets/events.js:
     *   { resource, sourceId, data: { itemCount, variantId, ... } }
     * This ensures cart-drawer-component and any other Horizon listeners
     * receive the exact shape they expect.
     */
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: this.form.id || '',
          data: {
            itemCount: cartData.item_count || 0,
            // Pass the main variant ID so per-product listeners can filter
            variantId: String(
              this.form.querySelector('[name="id"]')?.value || ''
            ),
          },
        },
      })
    );
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  /**
   * Set or clear the loading/disabled state on the Add to Cart button.
   * Matches the accessibility pattern used by Horizon's product-form-component.
   *
   * @param {HTMLElement|null} button
   * @param {boolean} isLoading
   */
  _setLoadingState(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    // aria-busy communicates loading state to screen readers
    button.setAttribute('aria-busy', String(isLoading));
  }

  /**
   * Display an error message using Horizon's built-in error surfaces:
   *   1. ref="liveRegion" (aria-live="assertive") – screen-reader accessible
   *   2. ref="addToCartTextError" (visible error span in the form)
   *
   * Both refs are set up in blocks/buy-buttons.liquid.
   *
   * @param {string} message
   */
  _showError(message) {
    // Live region for screen readers (div[ref="liveRegion"] in buy-buttons.liquid)
    const liveRegion = this.form
      .closest('product-form-component')
      ?.querySelector('[ref="liveRegion"]');
    if (liveRegion) {
      liveRegion.textContent = message;
    }

    // Visible error element (span[ref="addToCartTextError"] in buy-buttons.liquid)
    const visibleError = this.form.querySelector('[ref="addToCartTextError"]');
    if (visibleError) {
      visibleError.classList.remove('hidden');
      // The span contains an icon + a text child; update only the text child
      const errorTextNode = visibleError.querySelector('span:not(.svg-wrapper)');
      if (errorTextNode) errorTextNode.textContent = message;
    }
  }

  /**
   * Uncheck all addon toggles and clear the selection Map.
   * Called after a successful cart add so the UI resets cleanly.
   */
  _clearSelections() {
    this.selectedVariants.clear();
    this.container
      .querySelectorAll('[data-addon-variant]:checked')
      .forEach((checkbox) => {
        checkbox.checked = false;
        const variantId = parseInt(checkbox.value, 10);
        this._toggleExtraFields(variantId, false);
        // Remove extra engraving rows and clear field values
        this._resetEngravingRows(variantId);
      });
  }

  // ── Static lifecycle ──────────────────────────────────────────────────────

  /**
   * Find all uninitialised [data-addon-manager] containers and create an
   * AddonProductsManager for each.
   *
   * Idempotent: data-addon-initialized flag prevents double-instantiation,
   * which can otherwise occur during:
   *   – Shopify section rendering (SFR / dynamic section updates)
   *   – Theme editor live preview reloads
   *   – shopify:section:load / shopify:block:load events
   */
  static init() {
    document
      .querySelectorAll('[data-addon-manager]:not([data-addon-initialized])')
      .forEach((el) => {
        el.dataset.addonInitialized = 'true';
        new AddonProductsManager(el);
      });
  }
}

// ── Bootstrap ────────────────────────────────────────────────────────────────

// Initial page load: wait for DOM if not yet ready, otherwise run immediately.
// (type="module" scripts are deferred by default, so readyState is usually
// "interactive" or "complete" when this runs – the else branch handles it.)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', AddonProductsManager.init);
} else {
  AddonProductsManager.init();
}

// Step 10: Re-initialise after theme editor section/block reload events so the
// addon system keeps working in live preview without requiring a full page reload.
document.addEventListener('shopify:section:load', () => AddonProductsManager.init());
document.addEventListener('shopify:block:load',   () => AddonProductsManager.init());